import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import CopyToClipboardButton from "@/components/shared/CopyToClipboardButton";
import DownloadSection from "./DownloadSection";

const OutlookIntegration: React.FC<{
  dashboardUrl: string;
  projectDeveloper: string;
}> = ({ dashboardUrl, projectDeveloper }) => {
  const { t, language } = useLanguage();

  const projectDeveloperFolder =
    projectDeveloper === "Tree.ly, Dornbirn" ? "treely" : "wkss";

  const filesToDownload = [
    `/integrations/sustainabilityBadge/${projectDeveloperFolder}/${language}/sustainability-badge.png`,
    `/integrations/sustainabilityBadge/${projectDeveloperFolder}/${language}/sustainability-badge.svg`,
  ];
  const fileName = filesToDownload[0].split("/").pop();

  // Helper function to render HTML content safely
  const renderHTML = (htmlString: string) => {
    return { __html: htmlString };
  };

  return (
    <div className="space-y-6">
      <ol className="list-none space-y-8 p-0 m-0">
        {/* Step 1 */}
        <li className="relative pl-10">
          <div className="absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-semibold">
            1
          </div>
          <h3 className="text-base font-semibold mb-2">
            {t("integrationGuide.outlookIntegration.step1Title")}
          </h3>
          <p
            className="text-gray-700 mb-3"
            dangerouslySetInnerHTML={renderHTML(
              t("integrationGuide.outlookIntegration.step1Description")
            )}
          />
          <DownloadSection fileUrls={filesToDownload} />
        </li>

        {/* Step 2 */}
        <li className="relative pl-10">
          <div className="absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-semibold">
            2
          </div>
          <h3 className="text-base font-semibold mb-2">
            {t("integrationGuide.outlookIntegration.step2Title")}
          </h3>
          <p className="text-gray-700 mb-3">
            <span
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.outlookIntegration.step2DescriptionPart1")
              )}
            />
            <span className="text-gray-700" style={{ fontStyle: "italic" }}>
              {" "}
              (https://outlook.office.com)
            </span>
            <CopyToClipboardButton text="https://outlook.office.com" />{" "}
            <span
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.outlookIntegration.step2DescriptionPart2")
              )}
            />
          </p>
        </li>

        {/* Step 3 */}
        <li className="relative pl-10">
          <div className="absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-semibold">
            3
          </div>
          <h3 className="text-base font-semibold mb-2">
            {t("integrationGuide.outlookIntegration.step3Title")}
          </h3>
          <p className="text-gray-700 mb-3">
            <span
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.outlookIntegration.step3Description")
              )}
            />
          </p>

          <img
            src={`/integrations/outlookIntegration/${language}/step3.svg`}
            alt="Step 3"
            className="w-full h-full object-contain"
          />
        </li>

        {/* Step 4 */}
        <li className="relative pl-10">
          <div className="absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-semibold">
            4
          </div>
          <h3 className="text-base font-semibold mb-2">
            {t("integrationGuide.outlookIntegration.step4Title")}
          </h3>
          <ul className="list-disc pl-5 mb-3 space-y-1 text-gray-700">
            <li
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.outlookIntegration.step4DescriptionPoint1")
              )}
            />
            <li
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.outlookIntegration.step4DescriptionPoint2")
              )}
            />
            <li
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.outlookIntegration.step4DescriptionPoint3")
              )}
            />
          </ul>

          <div className="-ml-2.5">
            <img
              src={`/integrations/outlookIntegration/${language}/step4.svg`}
              alt="Step 4"
              className="w-full h-full object-contain"
            />
          </div>
        </li>

        {/* Step 5 */}
        <li className="relative pl-10">
          <div className="absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-semibold">
            5
          </div>
          <h3 className="text-base font-semibold mb-2">
            {t("integrationGuide.outlookIntegration.step5Title")}
          </h3>
          <ul className="list-disc pl-5 mb-3 space-y-1 text-gray-700">
            <li
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.outlookIntegration.step5DescriptionPoint1")
              )}
            />
            <li
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.outlookIntegration.step5DescriptionPoint2")
              )}
            />
            <li
              dangerouslySetInnerHTML={renderHTML(
                t(
                  "integrationGuide.outlookIntegration.step5DescriptionPoint3",
                  {
                    fileName: `<span style="font-style: italic">${fileName}</span>`,
                  }
                )
              )}
            />
            <li
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.outlookIntegration.step5DescriptionPoint4")
              )}
            />
          </ul>

          <div className="-ml-2.5">
            <img
              src={`/integrations/outlookIntegration/${language}/step5.svg`}
              alt="Step 5"
              className="w-full h-full object-contain"
            />
          </div>
        </li>

        {/* Step 6 */}
        <li className="relative pl-10">
          <div className="absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-semibold">
            6
          </div>
          <h3 className="text-base font-semibold mb-2">
            {t("integrationGuide.outlookIntegration.step6Title")}
          </h3>
          <p className="text-gray-700 mb-3">
            <span
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.outlookIntegration.step6Description")
              )}
            />
          </p>

          <div className="-ml-2.5">
            <img
              src={`/integrations/outlookIntegration/${language}/step6.svg`}
              alt="Step 6"
              className="w-full h-full object-contain"
            />
          </div>
        </li>

        {/* Step 7 */}
        <li className="relative pl-10">
          <div className="absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-semibold">
            7
          </div>
          <h3 className="text-base font-semibold mb-2">
            {t("integrationGuide.outlookIntegration.step7Title")}
          </h3>
          <ul className="list-disc pl-5 mb-3 space-y-1 text-gray-700">
            <li
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.outlookIntegration.step7DescriptionPoint1")
              )}
            />
            <li
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.outlookIntegration.step7DescriptionPoint2")
              )}
            />
            <span className="text-gray-700" style={{ fontStyle: "italic" }}>
              {dashboardUrl}
            </span>
            <CopyToClipboardButton text={dashboardUrl} />
            <li
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.outlookIntegration.step7DescriptionPoint3")
              )}
            />
          </ul>
          <div className="-ml-2.5">
            <img
              src={`/integrations/outlookIntegration/${language}/step7.svg`}
              alt="Step 7"
              className="w-full h-full object-contain"
            />
          </div>
        </li>

        {/* Step 8 */}
        <li className="relative pl-10">
          <div className="absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-semibold">
            8
          </div>
          <h3 className="text-base font-semibold mb-2">
            {t("integrationGuide.outlookIntegration.step8Title")}
          </h3>
          <p
            className="text-gray-700 mb-3"
            dangerouslySetInnerHTML={renderHTML(
              t("integrationGuide.outlookIntegration.step8Description")
            )}
          />
          <div className="-ml-2.5">
            <img
              src={`/integrations/outlookIntegration/${language}/step8.svg`}
              alt="Step 8"
              className="w-full h-full object-contain"
            />
          </div>
        </li>

        {/* Step 9 */}
        <li className="relative pl-10">
          <div className="absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-semibold">
            9
          </div>
          <h3 className="text-base font-semibold mb-2">
            {t("integrationGuide.outlookIntegration.step9Title")}
          </h3>
          <p
            className="text-gray-700 mb-3"
            dangerouslySetInnerHTML={renderHTML(
              t("integrationGuide.outlookIntegration.step9Description")
            )}
          />
        </li>
      </ol>
    </div>
  );
};

export default OutlookIntegration;
